import type { GameState } from './types'
import { teamSelectionIssue } from './injuries'

export type ActionStatus = 'required' | 'optional' | 'handled' | 'unavailable'

export interface WeeklyAction {
  phase: GameState['phase']
  label: string
  description: string
  status: ActionStatus
  statusLabel: string
  badge?: string
}

export const getWeeklyActions = (state: GameState): WeeklyAction[] => {
  const selectionIssue = teamSelectionIssue(state.staffPlan.team, state.club.players)
  const developmentAllocated = state.staffPlan.development.reduce((total, allocation) => total + allocation.points, 0)
  const developmentComplete = developmentAllocated === state.club.developmentCapacity
  const tacticalOverrides = state.tactics.filter(tactic => tactic.currentChoice !== tactic.staffChoice).length
    + (state.conditionalInstruction?.id === 'D7-A' ? 0 : 1)
  const researchCount = state.researchPlan.topics.filter(topic => topic.isSelected).length
  const selectionModified = state.decisions.D2.currentChoice !== 'A'
  const developmentModified = state.staffPlan.development.some(allocation => allocation.isLocked)

  const actions: WeeklyAction[] = [
    {
      phase: 'team',
      label: 'Team Selection',
      description: selectionIssue ?? (selectionModified ? 'The Coach has changed the selected side. This modified team will be used at match day.' : 'Staff have selected the 22 and assigned every position. Review only if you want to intervene.'),
      status: selectionIssue ? 'required' : 'optional',
      statusLabel: selectionIssue ? 'Action required — unavailable team' : selectionModified ? 'Review optional — Coach-modified team' : 'Review optional — staff team set',
      badge: '18+4',
    },
    {
      phase: 'development',
      label: 'Development',
      description: developmentComplete
        ? developmentModified ? 'The Coach has modified the development allocation. All capacity is assigned.' : 'Staff have allocated all development capacity. Their plan will apply unless you change it.'
        : `${state.club.developmentCapacity - developmentAllocated} development point${state.club.developmentCapacity - developmentAllocated === 1 ? '' : 's'} must be allocated before match day.`,
      status: developmentComplete ? 'optional' : 'required',
      statusLabel: developmentComplete ? developmentModified ? 'Review optional — Coach-modified plan' : 'Review optional — staff plan set' : 'Action required — allocation incomplete',
      badge: `${developmentAllocated}/${state.club.developmentCapacity} pts`,
    },
    {
      phase: 'tactics-review',
      label: 'Tactics',
      description: tacticalOverrides > 0
        ? `${tacticalOverrides} Coach override${tacticalOverrides === 1 ? '' : 's'} will replace the staff recommendation at match day.`
        : 'Staff have set ball movement, width, contests, defence, tempo and a conditional instruction.',
      status: 'optional',
      statusLabel: tacticalOverrides > 0 ? `Review optional — ${tacticalOverrides} Coach override${tacticalOverrides === 1 ? '' : 's'}` : 'Review optional — staff tactics set',
    },
    {
      phase: 'research',
      label: 'Research',
      description: researchCount
        ? `${researchCount} investigation${researchCount === 1 ? '' : 's'} selected; ${state.staffPlan.research.points} capacity committed and ${state.club.resources.research} retained.`
        : 'The free staff report is complete. Spend capacity only if you want higher-confidence tactical intelligence.',
      status: 'optional',
      statusLabel: researchCount ? `Review optional — ${researchCount} investigation${researchCount === 1 ? '' : 's'} active` : 'Review optional — staff report ready',
    },
    {
      phase: 'club',
      label: 'Club',
      description: state.clubStrategy.annualPlan.locked
        ? `Season ${state.clubStrategy.annualPlan.season} plan locked.`
        : `Season ${state.clubStrategy.annualPlan.season} planning.`,
      status: 'optional',
      statusLabel: state.clubStrategy.annualPlan.locked ? 'Season plan locked — review status' : 'Review optional — annual plan active',
    },
    {
      phase: 'staff',
      label: 'Staff',
      description: state.coachDevelopment.isLocked
        ? 'The coach-development plan is locked. No weekly action is needed.'
        : 'Staff have prepared a complete season development plan. Review it now or retain their allocations.',
      status: state.coachDevelopment.isLocked
        ? 'handled'
        : state.preseasonComplete === true
          ? 'required'
          : 'optional',
      statusLabel: state.coachDevelopment.isLocked
        ? 'Staff handled — season plan locked'
        : state.preseasonComplete === true
          ? 'Action required — annual coach plan not locked'
          : 'Review optional — staff plan set',
      badge: `${state.coachDevelopment.totalPoints} pts`,
    },
  ]

  return actions
}

export const isReadyForMatch = (state: GameState): boolean => {
  const actions = getWeeklyActions(state)
  const requiredActions = actions.filter(action => action.status === 'required')
  return requiredActions.length === 0
}

export const getRequiredActions = (state: GameState): WeeklyAction[] => {
  const actions = getWeeklyActions(state)
  return actions.filter(action => action.status === 'required')
}